import { use, useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import isEmail from "validator/lib/isEmail";
import { StackScreenProps } from "@react-navigation/stack";
import { useI18n } from "../../hooks/i18n-hooks";
import { AuthManagerContext } from "../../contexts/AuthManager";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { UPDATE_CLIENT_PROFILE } from "../../graphql/core";
import { useApolloErrorSnackbar } from "../../hooks/snackbar";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { useCloseEditor } from "../profile-editor/use-close-editor";
import { ClientProfile } from "../../__generated__/graphql";
import ScreenCard from "../ScreenCard";
import VStack from "../../ui_atoms/VStack";
import TextField from "../../components/TextField";
import PhoneField, { parsePhoneFieldValue } from "../../components/PhoneField";
import { BodyMediumText } from "../../components/FormattedText";

type FormData = {
    contactEmail?: string;
    contactPhoneNumber?: string;
};

type ClientContactEditorScreenProps = StackScreenProps<RootStackParamList, "ClientContactEdit">;

const ClientContactEditorScreen = ({ navigation }: ClientContactEditorScreenProps) => {
    const i18n = useI18n();
    const { refreshUser } = use(AuthManagerContext);
    const client = useCurrentProfile<ClientProfile>()?.client;

    const [updateClientProfile, { error: saveError, loading: saving, reset: saveReset }] =
        useMutation(UPDATE_CLIENT_PROFILE);

    useApolloErrorSnackbar(saveError, saveReset);

    const {
        handleSubmit,
        control,
        formState: { isValid, isDirty },
    } = useForm<FormData>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            contactEmail: client?.contactEmail || undefined,
            contactPhoneNumber: parsePhoneFieldValue(client?.contactPhoneNumber)?.number,
        },
    });

    const onSubmit = useCallback<SubmitHandler<FormData>>(
        async (data) => {
            try {
                // Both fields are optional; an emptied field is sent as null to clear it. The phone
                // number is normalised to E.164, the only format the backend accepts.
                await updateClientProfile({
                    variables: {
                        changes: {
                            client: {
                                contactEmail: data.contactEmail?.trim() || null,
                                contactPhoneNumber:
                                    parsePhoneFieldValue(data.contactPhoneNumber)?.number || null,
                            },
                        },
                    },
                });
                navigation.goBack();
                void refreshUser?.();
            } finally {
                // errors surface through the mutation error snackbar
            }
        },
        [updateClientProfile, navigation, refreshUser],
    );

    useCloseEditor(isDirty && !saving);

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("save"),
                    onPress: handleSubmit(onSubmit),
                    validating: saving,
                    isValid: isValid && isDirty,
                    mode: "secondary",
                },
            ]}
            withAssistanceButton
        >
            <VStack gap={0.5} alignment="stretch" h="2/3">
                <BodyMediumText>{i18n.t("screens.client_contact_edit.description")}</BodyMediumText>
                <VStack gap={0.25}>
                    <Controller
                        control={control}
                        name="contactEmail"
                        rules={{ validate: (v = "") => v === "" || isEmail(v) }}
                        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                            <TextField
                                label={i18n.t("mission_onboarding.contact_email")}
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                error={error?.message}
                                keyboardType="email-address"
                                textContentType="emailAddress"
                                autoComplete="email"
                                autoCorrect={false}
                                autoCapitalize="none"
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="contactPhoneNumber"
                        rules={{
                            validate: (v) => {
                                const parsed = parsePhoneFieldValue(v);
                                return (
                                    !parsed ||
                                    parsed.isValid() ||
                                    i18n.t("errors.field_name.phoneNumber")
                                );
                            },
                        }}
                        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                            <PhoneField
                                label={i18n.t("mission_onboarding.contact_phone")}
                                value={value}
                                onPhoneNumberChanged={onChange}
                                onBlur={onBlur}
                                error={error?.message}
                            />
                        )}
                    />
                </VStack>
            </VStack>
        </ScreenCard>
    );
};

export default ClientContactEditorScreen;
